import { HttpClient } from '@angular/common/http';
import { Injectable, ViewContainerRef } from '@angular/core';
import { NotificationService } from '../notification/notification.service';
import { DocumentData, FormParameters, User } from '@formbird/types';
import {TemplateHtmlService} from '../document/template-html.service';
import * as async from 'async';
import {isEmpty, startsWith} from 'lodash';

import loadjs from 'loadjs';

const logger = console;

@Injectable()
export class ComponentService {

  constructor(
    private http: HttpClient,
    private notificationService: NotificationService,
    private templateHtmlService: TemplateHtmlService
  ) {
  }

  loadedComponentDocs = {};
  public loadedComponents = {};
  private loadedVendorLibs = {};


  async loadVendorLibs(vendorLibs) {
    const fileNames = Object.keys(vendorLibs);
    return new Promise((resolve, reject) => {
      async.eachSeries(vendorLibs, (fileName, next) => {
        if (!this.loadedVendorLibs[fileName]) {
          const item = vendorLibs[fileName];
          loadjs([fileName], {
            returnPromise: true
          }).then(() => {
              this.loadedVendorLibs[fileName] = true;
              next(null);
            },
            (err) => {
              next();
              const msg = 'Error loading component vendor library: ' + fileName;
              console.log(msg);
              this.notificationService.error(msg);
            }
          );
        } else {
          next();
        }
      }, function (errorMsg) {
          if (errorMsg) {
            reject(new Error(errorMsg));
          }

          resolve();
      });
    });
  }

  async loadTemplateVendorLibs(template) {
    if (template && template.vendorLibrariesRel) {
      const templateVendorLibs = this.getFileNamesFromVendorLib(template.vendorLibrariesRel);
      await this.loadVendorLibs(templateVendorLibs);
    }
  }

  async registerComponents(componentDefs,
                           documentData: DocumentData,
                           formParameters: FormParameters,
                           user: User,
                           vcr: ViewContainerRef,
                           dynamicContainerComponent: any) {
    if (!componentDefs) {
      return Promise.resolve();
    }

    const _self = this;
    let key = 0;

    async.eachSeries(componentDefs, (componentDef, next) => {
      const directiveName = componentDef.componentName;
      if (directiveName && !startsWith(directiveName, 'ft-') &&  !startsWith(directiveName, 'cc-')) {
        const componentName = componentDef.componentName;
        const promise = _self.loadComponentDocAndVendorLibs(componentName).then(
          function successFunc() {
            componentDef.componentName = _self.componentDocExists(componentDef.componentName) ?
              componentDef.componentName : 'ft-component-not-found';
            _self.templateHtmlService.appendComponent(componentDef,
              documentData, formParameters, user, key, vcr, dynamicContainerComponent);
            key++;
            next();
          });
      }
    } , function (errorMsg) {
      if (errorMsg) {
        return Promise.reject(new Error(errorMsg));
      }
      return Promise.resolve();
    });
  }

  private getFileNamesFromVendorLib(vendorLibrariesRel) {
    const fileNames = [];
    if (vendorLibrariesRel && vendorLibrariesRel.length) {
      vendorLibrariesRel.forEach((vendorLib) => {
        if (vendorLib.fileName) {
          const fileName = vendorLib.fileName;
          if (!isEmpty(fileName) && !this.loadedVendorLibs[fileName]) {
            fileNames.push(fileName);
          }
        } else if (vendorLib.fileNames) {
          fileNames.push(...vendorLib.fileNames);
        }
      });
    }

    return fileNames;
  }

  /**
   * Load component document
   * @param directiveName the directive name
   * @returns the promise of component doc
   */
  async loadComponentDoc(directiveName) {
    const url = 'api/loadComponentDoc/' + directiveName;

    try {
      const result = await this.http.get(url).toPromise();
      if (result === 404) {
        throw new Error(`Component ${directiveName} not found.`);
      }

      return result;
    } catch (err) {
      throw new Error(err.message || 'Something went wrong when getting custom component document.');
    }
  }

  /**
   * whether the component has been loaded
   * @param directiveName the directive name
   * @returns the result
   */
  componentExists(directiveName) {

    return this.loadedComponents[directiveName];
  }

  /**
   * whether the component doc has been loaded
   * @param directiveName the directive name
   * @returns the result
   */
  componentDocExists(directiveName) {

    return !!this.loadedComponentDocs[directiveName];
  }

  loadComponentDocAndVendorLibs(componentName) {
     const _self = this;
    return new Promise((resolve, reject) => {
      if (!_self.componentDocExists(componentName)) {
        _self.loadComponentDoc(componentName).then(
          function successFunc(componentDoc: any) {
            _self.loadedComponentDocs[componentName] = componentDoc;

            const compVendorLibRels = componentDoc.vendorLibrariesRel;
            if (compVendorLibRels && compVendorLibRels.length) {
              const compVendorLibRel = componentDoc.vendorLibrariesRel;
              const compVendorLibs = _self.getFileNamesFromVendorLib(compVendorLibRel);

              _self.loadVendorLibs(compVendorLibs).then(
                function sFunc() {
                  resolve();
                },
                function eFunc() {
                  resolve();
                }
              );
            } else {
              resolve();
            }
          },
          function error(err) {
            _self.loadedComponentDocs[componentName] = null;
            // do not break the chain in order to load the next component document
            resolve();
          }
        );
      } else {
        resolve();
      }
    });
  }
}
